import { BaseSyntheticEvent, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ButtonPrimary, ButtonSecondary, Form, StackHorizontal } from '@talend/design-system';
import { getTheme } from '@talend/react-components';
import { SubHeaderBar } from '@talend/react-containers';
import Forms from '@talend/react-forms';

import { I18N_NAME_SPACE } from '../../../../constants';
import {
	CAD_TYPES,
	CADFormModel,
	CustomAttributeDefinition,
	CustomAttributeForm,
	FORM_MODE,
} from '../../CAD.types';
import { Types } from '../fields/Types';

import theme from './CADForm.module.css';

const css = getTheme(theme);

const CAD_FORM_ID = 'CAD-form';

const getDataFeatureSubmitButton = (type: CAD_TYPES) => {
	if (type === CAD_TYPES.TEXT) {
		return 'custom_attribute.text.add.save';
	}
	return 'custom_attribute.value_list.add.save';
};

function formValuesToDefinition(formValues: CustomAttributeForm): CustomAttributeDefinition {
	return {
		...formValues,
		description: formValues.description || null,
		defaults: formValues.defaults ? [formValues.defaults] : null,
		values: Array.isArray(formValues.values) ? formValues.values : null,
	};
}

function definitionToFormValues(definition: CustomAttributeDefinition): CustomAttributeForm {
	return {
		...definition,
		values:
			definition.values == undefined
				? undefined
				: Array.isArray(definition.values)
					? definition.values
					: [definition.values],
		defaults: Array.isArray(definition.defaults) ? definition.defaults[0] : definition.defaults,
	};
}

export interface CADFormProps {
	loading?: boolean;
	mode: FORM_MODE;
	onCancel: () => void;
	definition?: CustomAttributeDefinition;
	onSubmit: (definition: CustomAttributeDefinition) => void;
}

export function CADForm({
	loading = false,
	mode = FORM_MODE.ADD,
	onCancel,
	definition,
	onSubmit,
}: CADFormProps) {
	const { t } = useTranslation(I18N_NAME_SPACE);
	const rhf = useForm<CADFormModel>({
		mode: 'onBlur',
		defaultValues: {
			mandatory: false,
			type: CAD_TYPES.TEXT,
		},
	});

	useEffect(() => {
		if (mode !== FORM_MODE.ADD && definition && definition.id) {
			rhf.reset(definitionToFormValues(definition));
		}
	}, [definition, mode]);

	const onSubmitForm = (data: any, event?: BaseSyntheticEvent) => {
		event?.preventDefault();
		onSubmit(formValuesToDefinition(data));
	};

	const getTitleLabel = (formMode: FORM_MODE) => {
		switch (formMode) {
			case FORM_MODE.READ:
				return t('CUSTOM_ATTRIBUTE_READ_TITLE', 'Custom attribute');
			case FORM_MODE.ADD:
				return t('CUSTOM_ATTRIBUTE_ADD_FORM_TITLE', 'Add a new attribute');
			case FORM_MODE.EDIT:
				return t('CUSTOM_ATTRIBUTE_EDIT_FORM_TITLE', 'Edit an attribute');
			default:
				return '';
		}
	};

	const type = rhf.watch('type');
	return (
		<form id={`${mode}-${CAD_FORM_ID}`} onSubmit={rhf.handleSubmit(onSubmitForm)} noValidate>
			<FormProvider {...rhf}>
				<div>
					<SubHeaderBar
						title={getTitleLabel(mode)}
						onGoBack={onCancel}
						goBackDataFeature={`cad.${mode}.close`}
					/>
					<div className={getTheme('tdc-custom-attributes-form')}>
						{mode !== FORM_MODE.ADD && !(definition && definition.id) ? (
							<Forms.Skeleton displayMode="text" />
						) : (
							<>
								<div className={css('tdc-custom-attributes-form-content')}>
									<div className={css('tdc-custom-attributes-form-field')}>
										<Form.Text
											{...rhf.register('label', {
												required: t('ERROR_OBJECT_REQUIRED', 'Missing required field'),
											})}
											id={`${CAD_FORM_ID}-label`}
											data-testid={`${CAD_FORM_ID}-label`}
											placeholder={t(
												'CUSTOM_ATTRIBUTE_FORM_NAME_LABEL_PLACEHOLDER',
												'Name your attribute',
											)}
											required
											readOnly={mode === FORM_MODE.READ}
											hasError={!!rhf.formState.errors.label}
											description={
												rhf.formState.errors.label ? rhf.formState.errors.label.message : ''
											}
											label={t('CUSTOM_ATTRIBUTE_FORM_NAME_LABEL', 'Name')}
										/>
									</div>
									<div className={css('tdc-custom-attributes-form-field')}>
										<Form.Textarea
											{...rhf.register('description')}
											id={`${CAD_FORM_ID}-description`}
											placeholder={t(
												'CUSTOM_ATTRIBUTE_FORM_DESCRIPTION_LABEL_PLACEHOLDER',
												'Describe your attribute',
											)}
											label={t('CUSTOM_ATTRIBUTE_FORM_DESCRIPTION_LABEL', 'Description')}
											// rows="5"
											readOnly={mode === FORM_MODE.READ}
										/>
									</div>
									<div className={css('tdc-custom-attributes-form-field')}>
										<Form.Select
											{...rhf.register('type')}
											id={`${CAD_FORM_ID}-type`}
											onChange={e => rhf.setValue('type', e && e.target.value)}
											label={t('CUSTOM_ATTRIBUTE_FORM_SELECT_TYPE_LABEL', 'Type')}
											disabled={mode !== FORM_MODE.ADD}
											value={definition?.type}
											required
										>
											<option value={CAD_TYPES.TEXT}>{t('CAD_SELECT_TYPE_TEXT', 'Text')}</option>
											<option value={CAD_TYPES.LIST_OF_VALUES}>
												{t('CAD_SELECT_TYPE_LIST_OF_VALUES', 'List of values')}
											</option>
										</Form.Select>
									</div>

									<div className={css('tdc-custom-attributes-form-field')}>
										<Types readOnly={mode === FORM_MODE.READ} id={CAD_FORM_ID} rhf={rhf} />
									</div>
								</div>
							</>
						)}
					</div>
					<StackHorizontal gap="M" padding={{ top: 'M', left: 'XL', right: 0, bottom: 'M' }}>
						<ButtonSecondary disabled={loading} onClick={onCancel}>
							{t('CUSTOM_ATTRIBUTE_FORM_BUTTON_CANCEL', 'Cancel')}
						</ButtonSecondary>

						{mode !== FORM_MODE.READ && (
							<ButtonPrimary
								data-feature={getDataFeatureSubmitButton(type)}
								isLoading={loading}
								type="submit"
							>
								{t('CUSTOM_ATTRIBUTE_FORM_BUTTON_VALIDATE', 'Validate')}
							</ButtonPrimary>
						)}
					</StackHorizontal>
				</div>
			</FormProvider>
		</form>
	);
}

CADForm.displayName = 'CADForm';
